import { MultiSelect, NumberInput, Select, TextInput, Textarea } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { UseFormReturnType } from '@mantine/form';
import React from 'react';

import { CustomField, User } from 'types';

type CustomFieldInputProps = {
  field: CustomField;
  form: UseFormReturnType<Record<string, unknown>>;
  members?: User<string>[];
  value?: unknown;
  onChange?: (value: unknown) => void;
};

export function CustomFieldInput({
  field,
  form,
  members = [],
  value,
  onChange,
}: CustomFieldInputProps) {
  const fieldName = `customFields.${field.name}`;
  const customFields = form.values.customFields as Record<string, unknown> | undefined;
  const fieldValue = value !== undefined ? value : customFields?.[field.name];

  const validateField = (val: unknown): string | null => {
    if (field.required) {
      if (val === null || val === undefined) {
        return `${field.name} is required`;
      }
      if (typeof val === 'string' && val.trim() === '') {
        return `${field.name} is required`;
      }
      if (Array.isArray(val) && val.length === 0) {
        return `${field.name} is required`;
      }
    }
    return null;
  };

  const commonProps = {
    label: field.name,
    description: field.description,
    required: field.required,
    withAsterisk: field.required,
    error: validateField(fieldValue),
  };

  switch (field.type) {
    case 'text':
      return (
        <TextInput
          {...commonProps}
          value={(fieldValue as string) || ''}
          onChange={(e) => {
            const newValue = e.currentTarget.value;
            if (onChange) {
              onChange(newValue);
            } else {
              form.setFieldValue(fieldName, newValue);
              // Clear error when user starts typing
              if (field.required && newValue.trim()) {
                form.clearFieldError(fieldName);
              }
            }
          }}
          onBlur={() => {
            // Validate on blur
            const error = validateField(fieldValue);
            if (error) {
              form.setFieldError(fieldName, error);
            }
          }}
        />
      );

    case 'textarea':
      return (
        <Textarea
          {...commonProps}
          value={(fieldValue as string) || ''}
          onChange={(e) => {
            const newValue = e.currentTarget.value;
            if (onChange) {
              onChange(newValue);
            } else {
              form.setFieldValue(fieldName, newValue);
            }
          }}
          minRows={3}
        />
      );

    case 'number':
      return (
        <NumberInput
          {...commonProps}
          value={(fieldValue as number) || 0}
          onChange={(val) => {
            if (onChange) {
              onChange(val);
            } else {
              form.setFieldValue(fieldName, val);
            }
          }}
        />
      );

    case 'date':
      return (
        <DatePicker
          {...commonProps}
          value={fieldValue ? new Date(fieldValue as string) : null}
          onChange={(val) => {
            if (onChange) {
              onChange(val);
            } else {
              form.setFieldValue(fieldName, val);
            }
          }}
        />
      );

    case 'url':
      return (
        <TextInput
          {...commonProps}
          type="url"
          value={(fieldValue as string) || ''}
          onChange={(e) => {
            const newValue = e.currentTarget.value;
            if (onChange) {
              onChange(newValue);
            } else {
              form.setFieldValue(fieldName, newValue);
            }
          }}
        />
      );

    case 'select':
      return (
        <Select
          {...commonProps}
          data={field.options || []}
          value={(fieldValue as string) || null}
          onChange={(val) => {
            if (onChange) {
              onChange(val);
            } else {
              form.setFieldValue(fieldName, val);
              if (field.required && val) {
                form.clearFieldError(fieldName);
              }
            }
          }}
          onBlur={() => {
            const error = validateField(fieldValue);
            if (error) {
              form.setFieldError(fieldName, error);
            }
          }}
          clearable={!field.required}
        />
      );

    case 'multiselect':
      return (
        <MultiSelect
          {...commonProps}
          data={field.options || []}
          value={(fieldValue as string[]) || []}
          onChange={(val) => {
            if (onChange) {
              onChange(val);
            } else {
              form.setFieldValue(fieldName, val);
            }
          }}
        />
      );

    case 'user':
      return (
        <Select
          {...commonProps}
          data={members.map((member) => ({
            value: member._id,
            label: `${member.firstName} ${member.lastName}`,
          }))}
          value={(fieldValue as string) || null}
          onChange={(val) => {
            if (onChange) {
              onChange(val);
            } else {
              form.setFieldValue(fieldName, val);
              if (field.required && val) {
                form.clearFieldError(fieldName);
              }
            }
          }}
          onBlur={() => {
            const error = validateField(fieldValue);
            if (error) {
              form.setFieldError(fieldName, error);
            }
          }}
          searchable
          clearable={!field.required}
        />
      );

    default:
      return null;
  }
}
